import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useQuery } from '../../gql/hooks';
import {
  USAGE_SUMMARY_QUERY,
  type UsageSummaryQueryResult,
} from '../../gql/operations';
import type { UsageFilter } from '../../gql/types';
import { providerLabel } from '../shared/providerLabels';
import { formatNumber } from '../../lib/format';
import { Skeleton } from '../../ui';

// Reusable usage-tally card mounted on Client / Application / API-key detail surfaces so
// operators can read tokens + requests + cost at each tenancy level without leaving the
// page. Fetches `usageSummary(filter)` — the same pre-aggregated rollup the dashboard
// uses — scoped by whatever filter the caller passes (clientId / applicationId /
// apiKeyId). The aggregation is server-side, so this is a single round-trip regardless
// of how many usage rows underlie the scope.

export interface UsageTallyCardProps {
  /** Caller-supplied scope filter. The card refetches when this object identity changes. */
  filter: UsageFilter;
  /** Optional title override; defaults to "Usage". */
  title?: string;
  /** Optional one-line context shown under the title (e.g. "All-time"). */
  subtitle?: string;
}

export function UsageTallyCard({
  filter,
  title = 'Usage',
  subtitle = 'All-time totals',
}: UsageTallyCardProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // The hook keys on the variables object identity; memoize so a stable parent (e.g. a
  // route-loaded entity) doesn't refetch on every render.
  const variables = useMemo(() => ({ filter }), [filter]);
  const { data, loading, error } = useQuery<UsageSummaryQueryResult>(
    USAGE_SUMMARY_QUERY,
    variables,
  );

  return (
    <section
      data-testid="usage-tally-card"
      className={`rounded-2xl border p-5 ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-white'
      }`}
    >
      <header className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity
            className={`h-4 w-4 ${isDark ? 'text-white/50' : 'text-slate-500'}`}
            aria-hidden="true"
          />
          <h3
            className={`text-sm font-semibold ${isDark ? 'text-white/85' : 'text-slate-800'}`}
          >
            {title}
          </h3>
          <span className={`text-[11px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
            · {subtitle}
          </span>
        </div>
      </header>

      {loading && <Skeleton variant="block" height={108} />}

      {error && (
        <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
          Couldn't load usage: {error.message}
        </p>
      )}

      {data && !loading && (
        <>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <TallyTile
              isDark={isDark}
              label="Requests"
              value={formatNumber(data.usageSummary.totalRequests)}
              testId="usage-tally-requests"
            />
            <TallyTile
              isDark={isDark}
              label="Tokens"
              value={formatNumber(data.usageSummary.totalTokens)}
              testId="usage-tally-tokens"
            />
            <TallyTile
              isDark={isDark}
              label="Cost (est.)"
              value={
                data.usageSummary.totalCostEstimate == null
                  ? '—'
                  : `$${data.usageSummary.totalCostEstimate.toFixed(4)}`
              }
              testId="usage-tally-cost"
            />
          </div>

          {data.usageSummary.byProvider.length > 0 ? (
            <div>
              <p
                className={`mb-2 text-[10px] font-semibold uppercase tracking-widest ${
                  isDark ? 'text-white/40' : 'text-slate-400'
                }`}
              >
                By provider
              </p>
              <ul className="space-y-1.5">
                {data.usageSummary.byProvider.map((bucket) => (
                  <li
                    key={bucket.provider}
                    data-testid="usage-tally-provider-bucket"
                    data-provider={bucket.provider}
                    className={`flex items-baseline justify-between gap-3 rounded-md px-2.5 py-1.5 text-xs ${
                      isDark ? 'bg-white/[0.03]' : 'bg-slate-50'
                    }`}
                  >
                    <span
                      className={`font-medium ${isDark ? 'text-white/75' : 'text-slate-700'}`}
                    >
                      {providerLabel(bucket.provider)}
                    </span>
                    <span
                      className={`flex items-baseline gap-3 font-mono text-[11px] ${
                        isDark ? 'text-white/55' : 'text-slate-500'
                      }`}
                    >
                      <span>{formatNumber(bucket.requests)} req</span>
                      <span>{formatNumber(bucket.tokens)} tok</span>
                      <span
                        className={
                          bucket.failureRate > 0
                            ? isDark
                              ? 'text-rose-300'
                              : 'text-rose-600'
                            : isDark
                              ? 'text-white/35'
                              : 'text-slate-400'
                        }
                      >
                        {(bucket.failureRate * 100).toFixed(1)}% fail
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p
              className={`text-xs italic ${isDark ? 'text-white/30' : 'text-slate-400'}`}
            >
              No requests recorded for this scope yet.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function TallyTile({
  isDark,
  label,
  value,
  testId,
}: {
  isDark: boolean;
  label: string;
  value: string;
  testId: string;
}) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border px-3 py-3 ${
        isDark ? 'border-white/[0.05] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <span
        className={`block text-[10px] font-semibold uppercase tracking-widest ${
          isDark ? 'text-white/40' : 'text-slate-400'
        }`}
      >
        {label}
      </span>
      <span
        className={`mt-0.5 block font-mono text-base ${
          isDark ? 'text-white/85' : 'text-slate-800'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
